import { idx, rowsByIndex, store } from "@/store/store";
import type {
  FeedGroup,
  FeedPreference,
  File,
  Item,
  MembershipAvailability,
  Transcription,
  WorkspaceCommandAlias,
} from "@/store/types";

/**
 * Selector building blocks shared by the roster/list queries — the JS equivalents of the
 * subqueries the SQL repositories used to join (latest item, unread aggregate, first alias, feed
 * group, notification preference).
 */

/** Latest non-deleted item of a feed, optionally filtered (e.g. alias-member items only). */
export function latestItemForFeed(feedId: string, predicate?: (item: Item) => boolean): Item | null {
  let latest: Item | null = null;
  for (const item of rowsByIndex("item", idx.itemsByFeed, feedId)) {
    if (item.deletedAt != null) continue;
    if (predicate && !predicate(item)) continue;
    if (latest === null || item.createdAt > latest.createdAt) latest = item;
  }
  return latest;
}

/**
 * `bool_or(item.unread)` over a feed's non-deleted items: true when any is unread, false when
 * none are but at least one has a known flag, null when there are no rows or only null flags
 * (matching the SQL aggregate the sorts and filters were built on).
 */
export function feedHasUnread(feedId: string): boolean | null {
  let sawFlag = false;
  for (const item of rowsByIndex("item", idx.itemsByFeed, feedId)) {
    if (item.deletedAt != null) continue;
    if (item.unread == null) continue;
    if (item.unread) return true;
    sawFlag = true;
  }
  return sawFlag ? false : null;
}

/** Whether a membership holds an enabled `read` or `write` permission on a feed. */
export function membershipHasReadOrWrite(feedId: string, workspaceMembershipId: string): boolean {
  return rowsByIndex("permission", idx.permissionsByFeed, feedId).some(
    (p) => p.workspaceMembershipId === workspaceMembershipId && (p.name === "read" || p.name === "write") && p.enabled,
  );
}

/** The representative alias of a feed (the old DISTINCT ON (feedId) pick — first row wins). */
export function firstAliasForFeed(feedId: string): WorkspaceCommandAlias | null {
  for (const key of idx.commandAliasesByFeed.keys(feedId)) {
    const row = store.get("workspaceCommandAlias", key);
    if (row) return row;
  }
  return null;
}

/** The feed's group via its (unique per feed) group membership. */
export function groupForFeed(feedId: string): FeedGroup | null {
  for (const key of idx.feedGroupMembershipsByFeed.keys(feedId)) {
    const membership = store.get("feedGroupMembership", key);
    if (!membership) continue;
    const group = store.get("feedGroup", membership.groupId);
    if (group) return group;
  }
  return null;
}

/** A membership's preference row for a feed, optionally requiring push notifications enabled. */
export function preferenceForFeed(
  feedId: string,
  workspaceMembershipId: string,
  options?: { notifyingOnly?: boolean },
): FeedPreference | null {
  for (const key of idx.feedPreferencesByFeed.keys(feedId)) {
    const preference = store.get("feedPreference", key);
    if (!preference || preference.workspaceMembershipId !== workspaceMembershipId) continue;
    if (options?.notifyingOnly && preference.pushNotificationPreference === "none") continue;
    return preference;
  }
  return null;
}

/**
 * True when the membership holds an enabled `read` permission on the feed. AppSync keeps the local
 * permission rows live — including the disabled row the server publishes on revocation — so this
 * answers with the session's current access rather than what bootstrap saw. `expiresAt` is not
 * consulted, matching every other client-side permission check (the field is never synced).
 */
export function hasEnabledReadPermission(feedId: string, workspaceMembershipId: string): boolean {
  return rowsByIndex("permission", idx.permissionsByFeed, feedId).some(
    (permission) =>
      permission.workspaceMembershipId === workspaceMembershipId && permission.name === "read" && permission.enabled,
  );
}

/**
 * Latest non-deleted alias-member message timestamp per feed — the replacement for the removed
 * `feed.latestAliasActivity` denormalization (WEB-2133).
 */
export function latestAliasActivityForFeed(feedId: string): string | null {
  return latestItemForFeed(feedId, (item) => item.isFromAliasMember === true)?.createdAt ?? null;
}

/**
 * How well a transcription serves a reader of `language`: 0 for the language itself, 1 for another dialect of it
 * (Gemini writes regional codes like `es-MX`), 2 for the original the content was recorded in, and null for a
 * translation into some other language, which never serves.
 */
function transcriptionLanguageRank(row: Transcription, language: string): number | null {
  if (row.language === language) return 0;
  if (row.language.split("-")[0] === language.split("-")[0]) return 1;
  if (!row.translatedFrom) return 2;
  return null;
}

/**
 * The transcription of a content id that best serves a reader of `language`: the language itself, then another
 * dialect of it, then the original. Within a rank the higher priority wins, then the newer row. Null only when the
 * content has no transcription in any of those ranks. Format is not considered.
 */
export function transcriptionForContent(contentId: string | null | undefined, language: string): Transcription | null {
  if (contentId == null) return null;
  let best: Transcription | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const key of idx.transcriptionsByContent.keys(contentId)) {
    const row = store.get("transcription", key);
    if (!row) continue;
    const rank = transcriptionLanguageRank(row, language);
    if (rank === null) continue;
    if (best === null || rank < bestRank || (rank === bestRank && outranks(row, best))) {
      best = row;
      bestRank = rank;
    }
  }
  return best;
}

/** Whether `row` beats `other` of the same language rank: higher priority, then newer. */
function outranks(row: Transcription, other: Transcription): boolean {
  if (row.priority !== other.priority) return row.priority > other.priority;
  return (row.createdAt ?? "") > (other.createdAt ?? "");
}

/** The first file attached to a content id that carries a name; null when it has none. */
export function namedFileForContent(contentId: string | null | undefined): File | null {
  for (const file of rowsByIndex("file", idx.filesByContent, contentId)) {
    if (file.name != null) return file;
  }
  return null;
}

/** Whether a file is a photo: an `image/*` MIME type, the test the conversation view renders an image by. */
export function isPhotoFile(file: File | null): boolean {
  return file?.mimeType?.split("/")[0] === "image";
}

export function availabilityForMembership(
  workspaceMembershipId: string | null | undefined,
): MembershipAvailability | null {
  return store.get("wsMembershipAvailability", workspaceMembershipId ?? undefined) ?? null;
}

/** `NULLIF(CONCAT_WS(', ', NULLIF(municipality, ''), NULLIF(region, '')), '')` from the rosters. */
export function formatLocation(availability: MembershipAvailability | null | undefined): string | null {
  if (!availability) return null;
  const parts = [
    availability.mostRecentDeviceLocationNearestAddressMunicipality,
    availability.mostRecentDeviceLocationNearestAddressRegion,
  ].filter((part): part is string => part != null && part !== "");
  if (parts.length === 0) return null;
  return parts.join(", ");
}
